import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";

import "./faq.component.styles.scss";
import { useLang } from "../../../lib/i18n";
import { useReveal } from "../../../lib/motion";

type FaqItem = { q: string; a: string };

const FAQ_IT: FaqItem[] = [
  {
    q: "Come si prenota un trattamento?",
    a: "Puoi prenotare online: scegli il trattamento, scegli data e ora tra le disponibilità reali e paghi con carta. Oppure una telefonata o un messaggio WhatsApp al +39 349 246 8186 — ascoltiamo e scegliamo insieme.",
  },
  {
    q: "Quanto dura un massaggio?",
    a: "I nostri massaggi durano tipicamente 60 o 90 minuti. Scegli la durata direttamente quando prenoti online, oppure la concordiamo a voce in base al tempo che vuoi dedicarti.",
  },
  {
    q: "Dove si trova lo studio?",
    a: "A Sesto Fiorentino, in provincia di Firenze. Lo studio è privato, con parcheggio dedicato — raggiungibile facilmente anche in treno.",
  },
  {
    q: "Quali sono gli orari?",
    a: "Siamo aperti nei giorni feriali dalle 8:00 alle 18:00 e nei festivi dalle 9:00 alle 20:00. L'orario esatto varia in base alle prenotazioni: telefona per la disponibilità del giorno.",
  },
  {
    q: "È il mio primo massaggio — cosa mi consigliate?",
    a: "Il classico svedese o il massaggio emozionale sono il punto di partenza ideale. Parliamone al telefono: capiamo come stai e scegliamo un trattamento che ti faccia sentire a casa.",
  },
  {
    q: "Accettate gift card o regali?",
    a: "Sì, puoi regalare un trattamento o un ciclo di sedute. Contattaci e prepariamo insieme un pensiero su misura.",
  },
];

const FAQ_EN: FaqItem[] = [
  {
    q: "How do I book a treatment?",
    a: "Just call or send a WhatsApp message to +39 349 246 8186. We'll listen to what you need and choose the right ritual together.",
  },
  {
    q: "How long is a massage?",
    a: "Our massages typically last 60 or 90 minutes. On the first call, we agree on the duration based on the treatment type and how much time you'd like to dedicate to yourself.",
  },
  {
    q: "Where is the studio?",
    a: "In Sesto Fiorentino, just outside Florence (FI). The studio is private, with dedicated parking — easy to reach by train as well.",
  },
  {
    q: "What are your hours?",
    a: "We're open weekdays 8:00 AM to 6:00 PM and weekends 9:00 AM to 8:00 PM. Exact availability depends on bookings — call us to check the day's openings.",
  },
  {
    q: "It's my first massage — what do you recommend?",
    a: "The classic Swedish or the emotional massage are the ideal starting point. Let's talk on the phone: we'll understand how you feel and choose a treatment that puts you at ease.",
  },
  {
    q: "Do you offer gift cards?",
    a: "Yes — you can gift a treatment or a series of sessions. Contact us and we'll put together a thoughtful gift, tailored to the recipient.",
  },
];

const Faq: React.FC = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const { lang } = useLang();
  const isEn = lang === "en";
  const faq = isEn ? FAQ_EN : FAQ_IT;
  const headRef = useReveal<HTMLElement>();
  const listRef = useReveal<HTMLUListElement>();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: isEn ? "en" : "it-IT",
    mainEntity: faq.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <section className="lvs-faq" aria-labelledby="faq-title">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <div className="lvs-faq__inner lvs-container--narrow">
        <header className="lvs-faq__head lvs-reveal" ref={headRef}>
          <span className="eyebrow lvs-faq__eye">
            {isEn ? "Frequently asked questions" : "Domande frequenti"}
          </span>
          <h2 id="faq-title" className="lvs-faq__title">
            {isEn ? (
              <>
                Everything you might want to know,
                <br />
                <em>before saying yes.</em>
              </>
            ) : (
              <>
                Tutto ciò che vorresti sapere,
                <br />
                <em>prima di dire sì.</em>
              </>
            )}
          </h2>
        </header>

        <ul className="lvs-faq__list lvs-reveal-group" ref={listRef}>
          {faq.map(({ q, a }, i) => {
            const open = openIdx === i;
            return (
              <li key={q} className={`lvs-faq__item${open ? " is-open" : ""}`}>
                <button
                  type="button"
                  className="lvs-faq__q"
                  aria-expanded={open}
                  aria-controls={`faq-a-${i}`}
                  onClick={() => setOpenIdx(open ? null : i)}
                >
                  <span className="lvs-faq__num">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="lvs-faq__qText">{q}</span>
                  <span className="lvs-faq__icon" aria-hidden="true">
                    {open ? <MinusOutlined /> : <PlusOutlined />}
                  </span>
                </button>
                <div
                  id={`faq-a-${i}`}
                  className="lvs-faq__a"
                  hidden={!open}
                >
                  <p>{a}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default Faq;
